// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the three modes (principle #14). The RULE SET is the axis that changes the
// game, not a dialled number:
//   · Still  — a constant flinch threshold; bank each orb; a bust loses only that orb.
//   · Fever  — the threshold TIGHTENS as the charge grows (greed makes you twitchier),
//              a structurally different risk curve, not just bigger numbers.
//   · Streak — ONE life: a single bust ends the whole run, and each safe release raises
//              a multiplier applied to the next orb. Sudden-death chain — a different game.
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts);
// guests render state().hostOpts. An id off the wire is always modeOf()-validated so an
// unknown value falls back to Still rather than reaching the generator as undefined
// (guarded with Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'still' | 'fever' | 'streak';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** How many orbs a run lasts (Streak: the safety cap on an otherwise sudden-death chain). */
  readonly orbs: number;
  /** Streak: a single bust ends the run; each release grows the multiplier. */
  readonly oneLife: boolean;
  /** Per-release multiplier increment (Streak > 0; others 0). */
  readonly multStep: number;
  /** The hidden brim (the overflow time) is drawn uniformly in [brimMinMs, brimMaxMs]. */
  readonly brimMinMs: number;
  readonly brimMaxMs: number;
  /** Base flinch threshold on the 0..~1.4 motion-energy scale. Higher = more forgiving. */
  readonly tau0: number;
  /** Fever: the threshold also tightens as the charge grows toward feverRef. */
  readonly fever: boolean;
  /** How many jolts an orb schedules (clustered toward the brim). */
  readonly joltMin: number;
  readonly joltMax: number;
  /** Scales every jolt's startle intensity k. */
  readonly joltIntensity: number;
}

// ── shared physics constants (the same across modes; only the RULES above differ) ──
export const POT_A = 12; // charge grows: pot(s) = POT_A·s + POT_B·s²  (s = seconds held)
export const POT_B = 5;
export const GRACE_MS = 480; // no flinch check for this long after an orb starts (settle your grip)
export const STARTLE_MS = 640; // a jolt tightens the threshold for this long afterwards
export const STARTLE_TIGHTEN = 0.46; // a k=1 jolt cuts the threshold by this fraction
export const FEVER_TIGHTEN = 0.42; // Fever: max fraction the threshold tightens from a full charge
export const FEVER_REF = 210; // the charge at which Fever tightening saturates
export const AGITATION_NOISE = 0.11; // ± noise on the visible "tell" so reading is skilful, not exact
export const RELEASE_SUPPRESS_MS = 280; // flinch is ignored around a deliberate release tap (UI-side)

export const MODES: Record<ModeId, Mode> = {
  still: {
    id: 'still',
    name: 'Still',
    blurb: 'Five orbs. A steady threshold. Bank what you dare, spill only what you were holding.',
    orbs: 5,
    oneLife: false,
    multStep: 0,
    brimMinMs: 2700,
    brimMaxMs: 6800,
    tau0: 0.56,
    fever: false,
    joltMin: 3,
    joltMax: 5,
    joltIntensity: 0.82,
  },
  fever: {
    id: 'fever',
    name: 'Fever',
    blurb: 'Five orbs — but the fuller the light, the twitchier your hand. Greed tightens the nerve.',
    orbs: 5,
    oneLife: false,
    multStep: 0,
    brimMinMs: 2400,
    brimMaxMs: 6000,
    tau0: 0.52,
    fever: true,
    joltMin: 4,
    joltMax: 6,
    joltIntensity: 0.92,
  },
  streak: {
    id: 'streak',
    name: 'Streak',
    blurb: 'One life. One spill ends it. Every safe release multiplies the next — how far dare you push?',
    orbs: 20,
    oneLife: true,
    multStep: 0.35,
    brimMinMs: 2500,
    brimMaxMs: 6300,
    tau0: 0.54,
    fever: false,
    joltMin: 3,
    joltMax: 6,
    joltIntensity: 0.9,
  },
};

export const MODE_IDS: ModeId[] = ['still', 'fever', 'streak'];

export const DEFAULT_MODE: Mode = MODES.still;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Still. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
